// import Core from '../Core/Core';
import * as Sentry from '@sentry/browser';
import { isAndroid, isIOS } from 'mobile-device-detect';
import platform from '../Config/Platform';
import Core from '../Core/Core';
import getLogger, { ILogger } from '../Core/Logger';
import { FocusManager } from '../Managers/FocusManager';
import { waitForMs } from '../Utils/AsyncUtils';
import { ISDKpayments } from './ISDKpayments';
import { PayloadType } from './PayloadType';
import { ISDKads, SDKads } from './SDKads';
import { ISDKbots, SDKbots } from './SDKbots';
import { ISDKdata, SDKdata } from './SDKdata';
import { SDKfb } from './SDKfb';
import { ISDKleaderboards, SDKleaderboards } from './SDKleaderboards';
import { SDKlocal } from './SDKlocal';
import { ISDKmessages, SDKmessages } from './SDKmessages';
import { SDKnetwork } from './SDKnetwork';
import { SDKsg } from './SDKsg';
import { ISDKtracking, SDKtracking } from './SDKtracking';
import Loader = PIXI.loaders.Loader;
import SoundManager from '../Sound/SoundManager';

export let loadTime: number = 0;

const sdkLogger: ILogger = getLogger('[SDK]:');

export const enum Platform {
    Network = 'softgames',
    FBInstant = 'fbinstant',
    Local = 'local',
}

export interface ISGSettings {
    abTesting: {};
    bannerUrl: {};
    crossPromotion: {
        enabled: boolean;
    };
    env: {
        type: string;
        locale: string;
        anotherTab: boolean;
    };
    game: {
        id: string;
        slug: string;
    };
    invite: {
        native: boolean;
    };
    moreGames: {
        displayButton: boolean;
    };
    purchase: {
        available: boolean;
        native: boolean;
    };
    rewarded: {
        enabled: boolean;
    };
    share: {
        native: boolean;
    };
}

export interface IPayloadGiftData {
    giftData: Array<{
        amount: number;
        itemId: string;
    }>;
    id: string;
}

export interface IPayloadData {
    type?: PayloadType;
    userId?: string;
    userName?: string;
    rewardedUserId?: string;
    gift?: IPayloadGiftData;
    originGameId?: string;
    promoReward?: boolean;
    helperItem?: string;
}

export interface IEntryPointData {
    entryPoint: string;
    payload?: IPayloadData;
    originGameId?: string;
}

export const Triggers = {
    LOAD_COMPLETED: 'loading.completed',
    LOAD_UPDATE: 'loading.update',
};

export type SGHandler = any;

export interface ISGUser {
    avatar: string;
    name: string;
    userId: string;
    locale?: string;
    friends?: any;
    entryPointData?: any;
}

export interface ISGTrackData {
    event: string;
    dimension1: string;
    dimension2?: string;
    dimension3?: string;
    value?: number;
}

export interface ISDK {
    backend: SDKsg;
    platform: any;
}

// const logger: ILogger = getLogger('[Platform]: ');

export let sdkInitDone: number = 0;
export let loadStartTime: number;

export class SDK implements ISDK {
    public user: ISGUser;
    public backend: any;
    public platform: any;
    // new backend prop...used in conjunction with platform...

    public frozen: boolean = false;
    public gbConnected: boolean = false;

    public data: ISDKdata;
    public tracking: ISDKtracking;
    public ads: ISDKads;
    public bots: ISDKbots;
    public leaderboards: ISDKleaderboards;
    public messages: ISDKmessages;
    public payments: ISDKpayments;
    public adPlaying: boolean = false;

    public get gameId() {
        return `moji${SUFFIX ? `-${SUFFIX}` : ''}`;
    }

    private sgSettings: any;
    private sdkHandler: any;

    constructor() {
        if (window) {
            window.addEventListener('visibilitychange', this.checkFocus.bind(this));
            window.addEventListener('pagehide', this.checkFocus.bind(this));
            window.addEventListener('pageshow', this.checkFocus.bind(this));
            window.addEventListener('focus', this.checkFocus.bind(this));
            window.addEventListener('blur', this.checkFocus.bind(this));
            window.onblur = () => {
                this.checkFocus();
            };
            window.onfocus = () => {
                this.checkFocus();
            };
        }
        document.addEventListener('pagehide', this.checkFocus.bind(this));
        document.addEventListener('pageshow', this.checkFocus.bind(this));
        document.addEventListener('visibilitychange', this.checkFocus.bind(this));
        document.addEventListener('focus', this.checkFocus.bind(this));
        document.addEventListener('blur', this.checkFocus.bind(this));

        document.onfocus = () => {
            this.checkFocus();
        };
        document.onblur = () => {
            this.checkFocus();
        };
    }

    public async initSdk(targetPlatform: Platform, run: number = 0) {
        SoundManager.silentMusic(true);
        if (targetPlatform === Platform.FBInstant) {
            const options = platform.fbinstant;
            // add testPostfix to game in options here, so that the main game can be without one
            if (SUFFIX) {
                (options.game as any).testPostfix = SUFFIX as string;
            }

            if (run > 0) {
                await waitForMs(1000);
            }

            try {
                await FBInstant.initializeAsync();
                loadStartTime = Date.now();
                await this.initPlatform(targetPlatform);
            } catch (error) {
                if (run > 3) {
                    Sentry.captureMessage('facebook instant could not initialize');
                    throw error;
                } else {
                    sdkLogger.debug(['error with fb initializeAsync: ', error]);
                    await this.initSdk(targetPlatform, run + 1);
                }
            }
        } else if (targetPlatform === Platform.Network) {
            try {
                const oldSdkSettings: any = {
                    id: 'mahjong-story',
                    build: VERSION,
                    supportedLanguages: [
                        'en',
                        'de',
                        'es',
                        'fr',
                        'it',
                        'pt',
                        'ru',
                        'tr',
                        'nl',
                        'pl',
                        'vi',
                        'hi',
                        'th',
                    ],
                    changeLocale: (locale: string) => {
                        /* Core.changeLocale(locale)*/
                    },
                    freezeGame: () => this.freezeGame(),
                    goToLevel: () => {},
                    runGame: () => {
                        console.log('RUN GAME');
                        SoundManager.silentMusic(false);
                    },
                    unfreezeGame: () => this.unFreezeGame(),
                };

                const oldSdkModules: any = ['basic', 'levelGame'];
                await new Promise(
                    async (resolve: any, reject: any): Promise<any> => {
                        await window.sgSdk.initialize(
                            oldSdkModules,
                            oldSdkSettings,
                            async (err: any, settings: any, handler: any) => {
                                this.sgSettings = settings.config;
                                this.sdkHandler = handler;
                                loadStartTime = Date.now();
                                await this.initPlatform(targetPlatform);
                                resolve();
                            }
                        );
                    }
                );
            } catch (error) {
                const prefix: string = 'Failed to initialize sgSdk: ';
                if (error) {
                    if (error.message) {
                        error.message = prefix + error.message;
                    }
                    throw error;
                } else {
                    throw new Error(prefix + error);
                }
            }
        } else {
            await this.initPlatform(targetPlatform);
            return;
        }
    }

    public setInitialLoadTime(time: number) {
        loadTime = time;
    }

    // sdk initialize commands, only thing using state here so perhaps should be moved
    public freezeGame() {        
        FocusManager.instance.unFocus();
        this.frozen = true;
    }

    public unFreezeGame() {
        FocusManager.instance.focus();
        this.frozen = false;
    }

    // end initialize commands

    public async initPlatform(targetPlatform: Platform = Platform.Local) {
        if (targetPlatform === Platform.Network) {
            this.platform = new SDKnetwork(this.sgSettings, this.sdkHandler);
        } else if (targetPlatform === Platform.FBInstant) {
            this.platform = new SDKfb();
            this.backend = new SDKsg();
        } else {
            this.platform = new SDKlocal();
        }

        this.payments = this.platform.payments;
        this.data = new SDKdata(this.platform, this.backend);
        this.tracking = new SDKtracking(this.platform);
        this.ads = new SDKads(this.platform);
        this.bots = new SDKbots(this.platform);
        this.messages = new SDKmessages(this.platform, this.backend);
        this.leaderboards = new SDKleaderboards(this.backend);

        if (targetPlatform === Platform.FBInstant && this.backend) {
            const timeBeforeGBC = Date.now();
            this.backend
                .initGameBackend(this.platform.loadingPromise)
                .then(() => {
                    this.trackBotConversion();
                })
                .catch((err: any) => {
                    sdkLogger.debug(['backend init failed: ', err]);
                })
                .finally(() => {
                    const timeAfterGBC = Date.now();
                    const gbcDuration = timeAfterGBC - timeBeforeGBC;
                    FBInstant.logEvent('GBC_init_time', gbcDuration, {
                        time: gbcDuration.toString(),
                    });
                });
        }

        return;
    }

    public async loadProgress(loader: Loader) {
        const progressPercentage = Math.max(1, Math.min(100, loader.progress));
        this.platform.showLoadProgress(progressPercentage);
    }

    public async loadComplete(cb: () => void) {
        this.user = await this.platform.loadComplete();
        cb();
    }

    public getLocale() {
        return this.platform.getLocale();
    }

    public getEntryPointData() {
        return this.platform.getEntryPointData();
    }

    public checkBackendInitialized() {
        if (!this.backend) {
            return false;
        }

        return this.backend.isInitialized();
    }

    public async checkConnection() {
        if (!this.backend) {
            return false;
        }

        return this.backend.checkConnection();
    }

    public async trackBotConversion(): Promise<any> {
        if (!this.backend) {
            return false;
        }

        return this.backend.trackBotConversion();
    }

    public getPlatform() {
        return this.platform.getPlatform();
    }

    private checkFocus() {
        if (document.visibilityState === 'visible') {
            if (isIOS || isAndroid) {
                if (Core.sdk) {
                    Core.sdk.unFreezeGame();
                }
            } else {
                if (document.hasFocus()) {
                    if (Core.sdk) {
                        Core.sdk.unFreezeGame();
                    }
                } else {
                    if (Core.sdk) {
                        Core.sdk.freezeGame();
                    }
                }
            }
        } else {
            if (Core.sdk) {
                Core.sdk.freezeGame();
            }
        }
    }
}
